import { useEffect } from "react";

/**
 * Seo — lightweight head manager. No external deps, no hydration drama.
 * Updates <title>, meta description, canonical link and OG tags directly via
 * the DOM on mount and cleans up on unmount. Use one <Seo …/> per page.
 *
 * Why DOM-direct rather than react-helmet? It saves a dependency, avoids
 * any race with Suspense lazy chunks, and the SEO crawlers we care about
 * (Googlebot) execute JS and pick up the updated tags.
 */
export default function Seo({
  title,
  description,
  canonical,
  ogImage = "/hero-bowls-therapy.webp",
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", origin() + ogImage, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setLink("canonical", canonical || (typeof window !== "undefined" ? window.location.href.split("?")[0] : ""));

    let scriptEl;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.dataset.seoPage = "true";
      scriptEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }
    return () => {
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [title, description, canonical, ogImage, jsonLd]);

  return null;
}

function origin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export { Seo };
